import { useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';

interface Props {
  children: React.ReactNode;
}

const navItems = [
  { path: '/admin/products', icon: 'ri-layout-grid-line', label: '內容管理' },
];

export default function AdminLayout({ children }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    }
  }, [user, loading, navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#f0f2f5] flex items-center justify-center">
        <i className="ri-loader-4-line text-3xl text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#f0f2f5]">
      {/* Sidebar */}
      <aside className="w-56 bg-[#1a2535] flex flex-col flex-shrink-0">
        {/* Brand */}
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-[#1a73b5] flex items-center justify-center flex-shrink-0">
              <i className="ri-settings-3-line text-white text-sm" />
            </div>
            <div>
              <p className="text-white text-sm font-bold leading-none">益維後台</p>
              <p className="text-white/40 text-[10px] mt-0.5">Admin Panel</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {navItems.map(item => {
            const active = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-colors cursor-pointer ${
                  active
                    ? 'bg-[#1a73b5] text-white'
                    : 'text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-base`} />
                </div>
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="px-4 py-4 border-t border-white/10">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-7 h-7 bg-[#1a73b5]/30 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-line text-white/70 text-sm" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white/80 text-xs truncate">{user.email}</p>
              <p className="text-white/40 text-[10px]">管理員</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 text-white/50 hover:text-white/80 text-xs transition-colors cursor-pointer px-2 py-1.5 hover:bg-white/10"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-logout-box-line text-sm" />
            </div>
            登出
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-6 max-w-6xl">
          {children}
        </div>
      </main>
    </div>
  );
}
